import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';

import {
  getApiExchangeByEncryptedOrderIdCartOptions,
  getApiExchangeByEncryptedOrderIdCartQueryKey,
  getApiExchangeByEncryptedOrderIdEventsByEventIdOptions,
} from 'api/generated/@tanstack/react-query.gen';
import type { OrderResponse } from 'api/generated/types.gen';
import { useAppState } from 'lib/app-state';
import {
  getNewTicketsTotal,
  getReturnedTicketsTotal,
} from 'lib/helpers/exchange-helpers';

export type RefundTarget = 'giftCard' | 'donation';

export type ExchangeCart = ReturnType<typeof useExchangeCart>;

/**
 * The exchange layer of the cart. The newly chosen tickets are held in a
 * staging exchange order, which becomes the active order and renders through
 * the regular cart. Everything the swap adds on top — the tickets being
 * returned, the change fee, the amount due or refundable, and the refund
 * target — comes from here.
 *
 * `isExchange` is false when no exchange is in progress, leaving the cart as
 * the regular one.
 */
export function useExchangeCart() {
  const queryClient = useQueryClient();
  const { tempExchangeData, setTempExchangeData, eventData, setEventData } =
    useAppState();

  const exchangeOrderId = tempExchangeData?.exchangeOrderId ?? '';
  const order = eventData.order;

  const cartQuery = useQuery({
    ...getApiExchangeByEncryptedOrderIdCartOptions({
      path: { encryptedOrderId: exchangeOrderId },
    }),
    enabled: !!exchangeOrderId,
  });

  const cart = cartQuery.data;
  const stagingOrder = cart?.order;
  const summary = cart?.summary;

  // The staging order carries the exchange through the cart and checkout, so it
  // becomes the active order when the exchange starts. Picking it up here too
  // means a reload lands on the filled cart rather than the empty one. It runs
  // once per exchange so emptying the cart isn't undone by a later refetch.
  const hydratedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!stagingOrder?.encryptedId) return;
    if (hydratedRef.current === exchangeOrderId) return;
    hydratedRef.current = exchangeOrderId;
    setEventData((prev) =>
      prev.order?.encryptedId === stagingOrder.encryptedId
        ? prev
        : { ...prev, order: stagingOrder }
    );
  }, [stagingOrder, exchangeOrderId, setEventData]);

  // An exchange cart until the staging order is replaced by an ordinary one;
  // before hydration there is no order to compare against yet.
  const isExchange =
    !!exchangeOrderId && (!order || order.encryptedId === exchangeOrderId);
  const isLoading = !!exchangeOrderId && !order && cartQuery.isPending;

  const offersRefund =
    !!cart?.offersRefund && (cart?.refundableAmount ?? 0) > 0;

  const returnedTickets = cart?.returnedTickets ?? [];

  // The change rules — the refund targets (gift card group / donation ids) and
  // whether a refund is offered at all — live on the options response, which the
  // "choose a new event" step has already cached. Scoped to the same
  // subscription type the exchange was started under, or a different rule set
  // comes back with different refund targets.
  const rulesEnabled =
    isExchange &&
    !!tempExchangeData?.sourceEncryptedOrderId &&
    !!tempExchangeData?.sourceEventId;

  const optionsQuery = useQuery({
    ...getApiExchangeByEncryptedOrderIdEventsByEventIdOptions({
      path: {
        encryptedOrderId: tempExchangeData?.sourceEncryptedOrderId ?? '',
        eventId: tempExchangeData?.sourceEventId ?? 0,
      },
      query: tempExchangeData?.subscriptionTypeId
        ? { subscriptionTypeId: tempExchangeData.subscriptionTypeId }
        : undefined,
    }),
    enabled: rulesEnabled,
  });

  // The rules land independently of the cart, so a refundable swap can't be
  // judged settleable until they are in — otherwise finalize posts no target at
  // all. Only the cart decides whether a refund is on offer, so a swap that
  // gives nothing back never waits on them. A failed read counts as resolved so
  // the flow isn't stuck; the server rejects a missing target at finalize.
  const rulesResolved =
    !rulesEnabled ||
    !offersRefund ||
    optionsQuery.isSuccess ||
    optionsQuery.isError;

  const rules = optionsQuery.data?.rules;
  const refundGiftCardGroupIds = rules?.refundGiftCardGroupIds ?? [];
  const refundDonationIds = rules?.refundDonationIds ?? [];
  const canRefundGiftCard = refundGiftCardGroupIds.length > 0;
  const canRefundDonation = refundDonationIds.length > 0;

  // Selected refund target, mirrored into temp exchange data for finalize.
  const refundTarget: RefundTarget | null =
    tempExchangeData?.refundGiftCardGroupId != null
      ? 'giftCard'
      : tempExchangeData?.refundDonationId != null
        ? 'donation'
        : null;

  const setRefundTarget = useCallback(
    (value: RefundTarget) => {
      // With no rules there is no id to store, so the choice would be dropped
      // and the target left unset.
      const giftCardGroupId = rules?.refundGiftCardGroupIds?.[0] ?? null;
      const donationId = rules?.refundDonationIds?.[0] ?? null;
      // Several targets of the same kind are narrowed down on the refund list;
      // the first one stands in until then so the choice is never unset.
      if (value === 'giftCard' ? giftCardGroupId == null : donationId == null) {
        return;
      }
      setTempExchangeData((prev) => {
        if (!prev) return prev;
        if (value === 'giftCard') {
          return {
            ...prev,
            refundGiftCardGroupId: giftCardGroupId,
            refundDonationId: null,
          };
        }
        return {
          ...prev,
          refundGiftCardGroupId: null,
          refundDonationId: donationId,
        };
      });
    },
    [rules, setTempExchangeData]
  );

  const refundRequired =
    offersRefund && (canRefundGiftCard || canRefundDonation);

  // The change fee and the amount due are derived server-side, so anything that
  // can move them re-reads the exchange cart rather than adjusting it locally.
  const refreshCart = useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: getApiExchangeByEncryptedOrderIdCartQueryKey({
          path: { encryptedOrderId: exchangeOrderId },
        }),
      }),
    [exchangeOrderId, queryClient]
  );

  // Cart edits return the updated order only; the change fee and the amount due
  // are recalculated server-side, so the exchange cart is refetched after each.
  const applyOrder = useCallback(
    (next: OrderResponse | undefined) => {
      setEventData((prev) => ({
        event: next ? prev.event : undefined,
        order: next,
      }));
      refreshCart();
    },
    [refreshCart, setEventData]
  );

  return {
    isExchange,
    isLoading,
    /** Whether the priced swap has been read yet. Anything that branches on the
     *  amounts has to wait for it — before it settles they are all zero. */
    isPending: !!exchangeOrderId && cartQuery.isPending,
    returnedTickets,
    /** What the tickets handed back are worth with their tax, credited against
     *  the new ones. */
    returnedTicketsTotal: getReturnedTicketsTotal(returnedTickets),
    /** What the staged tickets are billed at with their tax, which is the
     *  returned tickets' value when the swap was price matched. */
    newTicketsTotal: getNewTicketsTotal(cart?.newTicketPrices ?? []),
    changeFee: summary?.changeFee ?? 0,
    /** Additive fees the new tickets carry net of the returned ones', on top of
     *  the change fee. Credits when the new tickets carry less. */
    serviceCharge: summary?.serviceCharge ?? 0,
    /** Tax on the fee lines only. The ticket lines carry their own, inside the
     *  returned and new totals. */
    salesTax: summary?.salesTax ?? 0,
    /** The ticket lines netted, tax inclusive. Negative on a cheaper swap. */
    subtotal: summary?.subtotal ?? 0,
    /** Amount to settle: what is owed, or what comes back on a cheaper swap.
     *  Negative when money comes back, so the summary reads it as a credit the
     *  way it reads the returned tickets and an applied gift card.
     *
     *  Deliberately not `summary.total`, which stays negative on a cheaper swap
     *  the rules give nothing back for. The saving is forfeited there and
     *  neither side settles, so what is shown has to be nothing, not a credit. */
    total: offersRefund
      ? -(cart?.refundableAmount ?? 0)
      : (cart?.amountDue ?? 0),
    /** What the swap costs on top of the returned tickets. Zero on an equal, a
     *  free or a cheaper swap, which settle without a payment. */
    amountDue: cart?.amountDue ?? 0,
    offersRefund,
    /** What a cheaper swap gives back, and so what the gift card or the donation
     *  it is refunded as is worth. Zero unless a refund is on offer. */
    refundableAmount: offersRefund ? (cart?.refundableAmount ?? 0) : 0,
    /** Whether the rules give nothing back when the new tickets cost less: the
     *  swap is price-matched to what was paid, with no refund. */
    noRefundOffered: isExchange && rules?.offerGiftCardOrDonation === false,
    refundRequired,
    canRefundGiftCard,
    canRefundDonation,
    /** Gift card groups the refund may be taken as. More than one is chosen
     *  between on the refund list rather than at checkout. */
    refundGiftCardGroupIds,
    /** Donations the refund may be given to, likewise. */
    refundDonationIds,
    refundTarget,
    setRefundTarget,
    /** Whether the exchange can move on to checkout. */
    canContinue: !!order,
    /** Whether the swap can be settled: a refundable swap needs a target first,
     *  chosen in the checkout's refund options. */
    refundReady: rulesResolved && (!refundRequired || refundTarget != null),
    applyOrder,
    refreshCart,
  };
}
